/**
 * Значение со статусом известности.
 *
 * Числовое поле само по себе не отличает «не знаю» от нуля. `needBaseline: 0`
 * означало и «расходов нет», и «человек не ответил» — и запас хода получался
 * бесконечным именно у того, кто ещё ничего не рассказал. Ноль, названный
 * человеком, и отсутствие ответа — разные факты, и путать их нельзя.
 *
 * Отсюда правило трёх ответов: число · ограниченный ответ · «недостаточно
 * данных» с указанием, какого факта не хватает. Тихая подстановка нуля
 * запрещена.
 */
import { ZERO, type Money } from './money.js';

/** Откуда взялось известное значение. */
export type AmountBasis = 'confirmed' | 'estimated' | 'history';

/** Почему значение неизвестно. */
export type UnknownReason = 'not_asked' | 'user_unknown';

export type Amount =
  | { known: true; value: Money; basis: AmountBasis }
  | { known: false; reason: UnknownReason };

/** Человек назвал это число сам. Ноль здесь — подтверждённый ноль. */
export const confirmed = (value: Money): Amount => ({ known: true, value, basis: 'confirmed' });

/** Оценка приложения: медиана, прикидка, значение по умолчанию. */
export const estimated = (value: Money): Amount => ({ known: true, value, basis: 'estimated' });

/** Посчитано по фактической истории. */
export const fromHistory = (value: Money): Amount => ({ known: true, value, basis: 'history' });

export const unknownAmount = (reason: UnknownReason = 'not_asked'): Amount =>
  ({ known: false, reason });

export const isKnown = (a: Amount): a is { known: true; value: Money; basis: AmountBasis } => a.known;

/**
 * Число или запасной вариант.
 *
 * Единственный способ получить `Money` из `Amount` — и он требует явно
 * назвать, что делать при незнании. Поэтому подстановка нуля перестаёт быть
 * случайной: она видна в коде.
 */
export const valueOr = (a: Amount, fallback: Money): Money => (a.known ? a.value : fallback);

/** Значение, если известно. Иначе `null` — для мест, где нуля быть не должно. */
export const valueOrNull = (a: Amount): Money | null => (a.known ? a.value : null);

/**
 * Ответ расчёта, которому не хватило данных.
 *
 * Содержит не только признак, но и **что именно спросить**: «уточни обычные
 * расходы» полезнее, чем «недостаточно данных».
 */
export interface InsufficientData {
  known: false;
  /** Поля, без которых ответа нет. */
  missing: string[];
  /** Одно понятное действие для человека. */
  action: string;
}

export type Answer<T> = ({ known: true } & T) | InsufficientData;

/**
 * Собрать несколько значений разом.
 *
 * Если неизвестно хотя бы одно — результат неизвестен, а не ноль. Имена полей
 * возвращаются, чтобы спросить именно о них.
 */
export function requireAll(
  fields: Readonly<Record<string, Amount>>,
): { known: true; values: Record<string, Money> } | { known: false; missing: string[] } {
  const values: Record<string, Money> = {};
  const missing: string[] = [];
  for (const [name, a] of Object.entries(fields)) {
    if (a.known) values[name] = a.value;
    else missing.push(name);
  }
  return missing.length > 0 ? { known: false, missing } : { known: true, values };
}

/** Сумма известных значений. Неизвестные делают неизвестной и сумму. */
export function addAmounts(...xs: Amount[]): Amount {
  let total = ZERO;
  for (const x of xs) {
    if (!x.known) return unknownAmount(x.reason);
    total = (total + x.value) as Money;
  }
  // Складывали известные — значит, результат посчитан, а не назван человеком.
  return xs.every((x) => x.known && x.basis === 'confirmed') ? confirmed(total) : estimated(total);
}

/**
 * Прочитать значение из старых данных.
 *
 * До введения статусов отсутствие значения и ноль записывались одинаково.
 * Число читается как подтверждённое, отсутствие — как «не спрашивали»:
 * нельзя задним числом объявить, что человек ответил.
 */
export const amountFromLegacy = (value: Money | null | undefined): Amount =>
  (value === null || value === undefined ? unknownAmount('not_asked') : confirmed(value));
